<template>
  <div ref="sliderRef" class="chess-dates-slider" :class="{ 'chess-dates-slider--loading': props.loading }">
    <v-slider
      v-model="scrollProgress"
      hide-details
      thumb-size="58"
      :ripple="false"
      elevation="0"
      @start="emit('start')"
      @end="emit('end')"
    />

    <div class="chess-dates-slider__ticks">
      <template v-if="props.loading">
        <div class="chess-dates-slider__tick" v-for="item in 10" :key="item">
          <skeleton-loader class="w-100" height="16px" />
        </div>
      </template>

      <template v-else>
        <button
          v-for="(tick, index) in ticksLabels"
          :key="index"
          class="chess-dates-slider__tick"
          @click="handleTickClick(tick.date)"
        >
          {{ tick.formattedDate }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { RangeOfDateOnly } from '@/graphql/generated';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';

const props = defineProps<{
  period?: RangeOfDateOnly;
  loading?: boolean;
}>();

const scrollProgress = defineModel<number>('scrollProgress', { required: true });

const emit = defineEmits<{
  (event: 'start'): void;
  (event: 'end'): void;
  (event: 'click:tick', date: string): void;
}>();

const sliderRef = ref();
const sliderResizeObserver = ref<ResizeObserver>();
const sliderMaxLabels = ref(0); // Максимальное число лейблов выводимое на таймлайн исходя из ширины таймлайна

const ticks = computed(() => {
  if (!props.period) {
    return [];
  }

  let items: string[] = [];

  let dateStart = dayjs(props.period.from);
  const dateEnd = dayjs(props.period.to);

  while (dateEnd.diff(dateStart, 'months') >= 0) {
    const startOfMonth = dateStart.startOf('month').format('YYYY-MM-DD');
    const startDay = startOfMonth < props.period.from ? props.period.from : startOfMonth;
    const endOfMonth = dateStart.endOf('month').format('YYYY-MM-DD');
    const endDay = endOfMonth > props.period.to ? props.period.to : endOfMonth;

    items = [...items, ...getDays(startDay, endDay)];

    dateStart = dateStart.add(1, 'month');
  }

  return items;
});

const ticksSettings = computed(() => {
  let step: number | 'month';
  let additionalMonthCount = 0;

  if (Math.floor(ticks.value.length / 10) < sliderMaxLabels.value) {
    step = 10;
  } else if (Math.floor(ticks.value.length / 15) < sliderMaxLabels.value) {
    step = 15;
  } else if (Math.floor(ticks.value.length / 30) < sliderMaxLabels.value) {
    step = 'month';
    additionalMonthCount = 1;
  } else if (Math.floor(ticks.value.length / 60) < sliderMaxLabels.value) {
    step = 'month';
    additionalMonthCount = 2;
  } else if (Math.floor(ticks.value.length / 90) < sliderMaxLabels.value) {
    step = 'month';
    additionalMonthCount = 3;
  } else {
    step = 'month';
    additionalMonthCount = 4;
  }

  return {
    step,
    additionalMonthCount,
    dateFormat: step === 'month' ? 'MMM, YY' : 'DD.MM.YY',
  };
});

const ticksLabels = computed(() => {
  const result: Record<number, { date: string; formattedDate: string }> = {};

  for (let i = 0; i < ticks.value.length; ) {
    result[i] = {
      date: ticks.value[i],
      formattedDate: dayjs(ticks.value[i]).format(ticksSettings.value.dateFormat),
    };

    if (ticksSettings.value.step === 'month') {
      for (let j = 1; j <= ticksSettings.value.additionalMonthCount; j++) {
        i += dayjs(ticks.value[i]).add(j, 'month').daysInMonth();
      }
    } else {
      i += ticksSettings.value.step;
    }
  }

  result[ticks.value.length - 1] = {
    date: ticks.value[ticks.value.length - 1],
    formattedDate: dayjs(ticks.value[ticks.value.length - 1]).format(ticksSettings.value.dateFormat),
  };

  return result;
});

onMounted(() => {
  sliderResizeObserver.value = new ResizeObserver(() => {
    sliderMaxLabels.value = Math.floor(sliderRef.value?.offsetWidth / 90);
  });

  sliderResizeObserver.value.observe(sliderRef.value);
});

onBeforeUnmount(() => {
  sliderResizeObserver.value?.unobserve(sliderRef.value);
});

const handleTickClick = (date: string) => {
  if (!props.period) {
    return;
  }

  const dateStartOfMonth = dayjs(date).startOf('month').format('YYYY-MM-DD');
  emit('click:tick', dateStartOfMonth < props.period.from ? props.period.from : dateStartOfMonth);
};

const getDays = (start: string, end: string) => {
  const days = [];
  let dateStart = dayjs(start);
  const dateEnd = dayjs(end);

  while (dateEnd.diff(dateStart, 'days') >= 0) {
    days.push(dateStart.format('YYYY-MM-DD'));
    dateStart = dateStart.add(1, 'day');
  }

  return days;
};
</script>

<style scoped lang="scss">
.chess-dates-slider {
  $thumb-width: 58px;
  $thumb-height: 20px;

  &__ticks {
    display: flex;
    justify-content: space-between;

    margin-top: 2px;
  }

  &__tick {
    display: flex;

    width: 100%;
    max-width: 94px;
    height: 16px;
    margin-left: 24px;
    padding-top: 3px;
    padding-left: 7px;

    font-size: 10px;
    line-height: 12px;
    font-weight: 600;
    color: $text-ordinary-color;
    word-break: break-word;

    border-left: 1px solid $border-color-light;

    cursor: pointer;
    transition: color $transition-duration;

    @include hover {
      color: $primary-color;
    }
  }

  :deep(.v-slider) {
    margin: 0 calc($thumb-width / 2) + 4px;

    .v-input__control {
      min-height: initial;
    }

    .v-slider-track {
      height: calc($thumb-height + 4px);
    }

    .v-slider-track__background {
      width: calc(100% + $thumb-width + 4px) !important;
      height: inherit;
      margin-left: calc($thumb-width / -2 - 2px);

      background-color: $bg-light-color;

      opacity: 1;
    }

    .v-slider-track__fill {
      display: none;
    }

    .v-slider-thumb {
      transition: 0s;
    }

    .v-slider-thumb__surface {
      height: $thumb-height;

      background: url('data:image/svg+xml,<svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L1 9" stroke="%232B6EF2" stroke-width="2" stroke-linecap="round"/><path d="M5.86133 1L5.86133 9" stroke="%232B6EF2" stroke-width="2" stroke-linecap="round"/><path d="M11 1L11 9" stroke="%232B6EF2" stroke-width="2" stroke-linecap="round"/></svg>')
        no-repeat center center / 12px auto $primary-tonal-color;
      border-radius: $border-radius;

      &::before {
        display: none;
      }
    }

    .v-slider-thumb__ripple {
      display: none;
    }
  }

  &--loading {
    pointer-events: none;
  }
}
</style>
